import React, {ReactNode} from 'react';
import ComponentName from '../constant/ComponentName';
import LanguageUtil from './LanguageUtil';
import MessageUtil from './MessageUtil';

export interface NotDeployedDependency {
  componentType: string;
  componentId: string;
}

interface NotDeployedErrorBody {
  reason: string;
  dependencies: NotDeployedDependency[];
}

class DeployErrorUtil {
  private static readonly pendingCheckerReason = 'PENDING_CHECKER_REQUEST';

  public static show(error: unknown, summary: string): boolean {
    if (DeployErrorUtil.showPendingCheckerRequest(error, summary)) {
      return true;
    }
    const dependencies = DeployErrorUtil.readDependencies(error);
    if (dependencies == undefined) {
      return false;
    }
    DeployErrorUtil.showDependencies(dependencies, summary);
    return true;
  }

  private static showPendingCheckerRequest(error: unknown, summary: string): boolean {
    if (!DeployErrorUtil.isPendingCheckerRequest(error)) {
      return false;
    }
    MessageUtil.error({
      life: 8000,
      summary,
      detail: LanguageUtil.get({
        id: 'messages.error.pendingCheckerRequest',
        defaultMessage: 'This component already has a pending approval request. A new deploy, undeploy, or delete request cannot be sent until that request is approved or rejected.',
      }),
    });
    return true;
  }

  public static showApprovalWarning(payload: unknown): void {
    const dependencies = DeployErrorUtil.readWarningList(payload);
    const checkFailed = DeployErrorUtil.readWarningCheckFailed(payload);
    if (dependencies.length === 0 && !checkFailed) {
      return;
    }
    const summaryId = checkFailed
      ? 'messages.info.deployWarningCheckFailed'
      : 'messages.info.deployApprovalMayFail';
    const summaryDefault = checkFailed
      ? 'The dependency check could not be completed. The request was sent, but it may be rejected during approval.'
      : 'The request was sent, but it may be rejected during approval.';
    MessageUtil.info({
      life: 8000,
      summary: LanguageUtil.get({id: summaryId, defaultMessage: summaryDefault}),
      detail: dependencies.length > 0 ? DeployErrorUtil.render(dependencies) : undefined,
    });
  }

  public static showDependencies(dependencies: NotDeployedDependency[], summary: string): void {
    const unique = DeployErrorUtil.unique(dependencies);
    if (unique.length === 0) {
      return;
    }
    MessageUtil.error({
      summary,
      detail: DeployErrorUtil.render(unique),
    });
  }

  public static readDependencies(error: unknown): NotDeployedDependency[] | undefined {
    const body = DeployErrorUtil.readBody(error);
    if (body == undefined) {
      return undefined;
    }
    const dependencies: NotDeployedDependency[] = [];
    for (const item of body.dependencies) {
      if (!DeployErrorUtil.isDependency(item)) {
        continue;
      }
      dependencies.push({
        componentType: item.componentType,
        componentId: item.componentId,
      });
    }
    if (dependencies.length === 0) {
      return undefined;
    }
    return dependencies;
  }

  private static readWarningList(payload: unknown): NotDeployedDependency[] {
    if (!DeployErrorUtil.isRecord(payload)) {
      return [];
    }
    const warnings = payload['warnings'];
    if (!Array.isArray(warnings)) {
      return [];
    }
    const dependencies: NotDeployedDependency[] = [];
    for (const item of warnings) {
      if (!DeployErrorUtil.isDependency(item)) {
        continue;
      }
      dependencies.push({
        componentType: item.componentType,
        componentId: item.componentId,
      });
    }
    return DeployErrorUtil.unique(dependencies);
  }

  private static readWarningCheckFailed(payload: unknown): boolean {
    if (!DeployErrorUtil.isRecord(payload)) {
      return false;
    }
    return payload['warningCheckFailed'] === true;
  }

  private static unique(dependencies: NotDeployedDependency[]): NotDeployedDependency[] {
    const seen = new Set<string>();
    const unique: NotDeployedDependency[] = [];
    for (const dependency of dependencies) {
      const key = `${dependency.componentType}:${dependency.componentId}`;
      if (seen.has(key)) {
        continue;
      }
      seen.add(key);
      unique.push(dependency);
    }
    return unique;
  }

  private static render(dependencies: NotDeployedDependency[]): ReactNode {
    return (
      <div>
        {dependencies.map((dependency, index) => (
          <div key={`${dependency.componentType}-${dependency.componentId}-${index}`}>
            {DeployErrorUtil.formatLine(dependency)}
          </div>
        ))}
      </div>
    );
  }

  private static formatLine(dependency: NotDeployedDependency): string {
    const component = DeployErrorUtil.resolveLabel(dependency.componentType);
    const message = LanguageUtil.get(
      {id: 'messages.error.selectedNotDeployed', defaultMessage: 'Selected {component} is not deployed.'},
      {component},
    );
    if (dependency.componentId.length === 0) {
      return message;
    }
    return `${message} (${dependency.componentId})`;
  }

  private static resolveLabel(componentType: string): string {
    switch (componentType) {
      case ComponentName.STREAM:
        return LanguageUtil.get({id: 'common.label.store', defaultMessage: 'Store'});
      case ComponentName.TRANSACTION:
        return LanguageUtil.get({id: 'common.label.channel', defaultMessage: 'Channel'});
      case ComponentName.CONNECTION:
        return LanguageUtil.get({id: 'common.label.connection', defaultMessage: 'Connection'});
      case ComponentName.LIST:
        return LanguageUtil.get({id: 'common.label.list', defaultMessage: 'List'});
      case ComponentName.ACTION:
        return LanguageUtil.get({id: 'common.label.action', defaultMessage: 'Action'});
      case ComponentName.RULE:
        return LanguageUtil.get({id: 'common.label.rule', defaultMessage: 'Rule'});
      case ComponentName.AI_MODEL:
        return LanguageUtil.get({id: 'common.label.aiModel', defaultMessage: 'Ai Model'});
      case ComponentName.BASIC_VARIABLE:
      case ComponentName.API_VARIABLE:
      case ComponentName.QUERY_VARIABLE:
      case ComponentName.LIST_VARIABLE:
      case ComponentName.VELOCITY:
      case ComponentName.ENRICHMENT:
        return LanguageUtil.get({id: 'common.label.enrichment', defaultMessage: 'Enrichment'});
      default:
        return componentType;
    }
  }

  private static readBody(error: unknown): NotDeployedErrorBody | undefined {
    if (!DeployErrorUtil.isRecord(error)) {
      return undefined;
    }
    if (DeployErrorUtil.isNotDeployedBody(error)) {
      return error;
    }
    const response = error['response'];
    if (!DeployErrorUtil.isRecord(response)) {
      return undefined;
    }
    const data = response['data'];
    if (DeployErrorUtil.isNotDeployedBody(data)) {
      return data;
    }
    return undefined;
  }

  private static isPendingCheckerRequest(error: unknown): boolean {
    const body = DeployErrorUtil.readResponseBody(error);
    if (body == undefined) {
      return false;
    }
    if (body['reason'] === DeployErrorUtil.pendingCheckerReason) {
      return true;
    }
    return body['message'] === 'There is already a request for this document';
  }

  private static readResponseBody(error: unknown): Record<string, unknown> | undefined {
    if (!DeployErrorUtil.isRecord(error)) {
      return undefined;
    }
    const response = error['response'];
    if (DeployErrorUtil.isRecord(response)) {
      const data = response['data'];
      if (DeployErrorUtil.isRecord(data)) {
        return data;
      }
    }
    if (typeof error['reason'] === 'string' || typeof error['message'] === 'string') {
      return error;
    }
    return undefined;
  }

  private static isNotDeployedBody(value: unknown): value is NotDeployedErrorBody {
    if (!DeployErrorUtil.isRecord(value)) {
      return false;
    }
    return value['reason'] === 'NOT_DEPLOYED' && Array.isArray(value['dependencies']);
  }

  private static isDependency(value: unknown): value is NotDeployedDependency {
    if (!DeployErrorUtil.isRecord(value)) {
      return false;
    }
    return typeof value['componentType'] === 'string' && typeof value['componentId'] === 'string';
  }

  private static isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null;
  }
}

export default DeployErrorUtil;
